import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { forwardRef } from 'react';

import { cn } from '@/lib/utils';

const typographyVariants = cva('', {
  variants: {
    variant: {
      h1: 'scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl',
      h2: 'mb-4 scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0',
      h3: 'scroll-m-20 text-2xl font-semibold tracking-tight',
      h4: 'scroll-m-20 text-xl font-semibold tracking-tight',
      p: 'leading-6',
      blockQuote: 'mt-6 border-l-2 pl-6 italic',
      inlineCode:
        'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
      label: 'text-sm font-medium leading-none',
      muted: 'text-base text-muted-foreground',
      subtitle: 'text-sm text-muted-foreground',
    },
  },
  defaultVariants: {
    variant: 'p',
  },
});

const componentTypes = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  p: 'p',
  blockQuote: 'blockquote',
  inlineCode: 'code',
  label: 'label',
  muted: 'span',
  subtitle: 'span',
};

type TypographyProps = VariantProps<typeof typographyVariants> &
  React.HTMLAttributes<HTMLParagraphElement> & {
    asChild?: boolean;
  };

export const Typography = forwardRef<HTMLParagraphElement, TypographyProps>(
  ({ variant, className, ...props }: TypographyProps, ref) => {
    const Comp = props.asChild ? Slot : componentTypes[variant || 'p'];

    return (
      <Comp
        ref={ref}
        className={cn(typographyVariants({ variant }), className)}
        {...props}
      />
    );
  },
);
